import useDesignTokens from '@assetron/web-common/themes/useDesignTokens';
import { useTheme } from '@mui/material';
import { useMemo } from 'react';

const useAuthAssetronInputFieldStyles = (
  disabled: boolean,
  destructive: boolean,
) => {
  const theme = useTheme();
  const tokens = useDesignTokens();

  const styles = useMemo(() => {
    const getBorderColor = () => {
      if (disabled) return `${tokens.colors.border.disabled} !important`;
      if (destructive) return `${tokens.colors.border.error} !important`;
      return `${tokens.colors.border.tertiary} !important`;
    };

    const getFocusedBorderColor = () => {
      if (disabled) return `${tokens.colors.border.disabled} !important`;
      if (destructive) return `${tokens.colors.border.error} !important`;
      return `${tokens.colors.border.highlightSolid} !important`;
    };

    const getHoverCursor = () => {
      return disabled ? 'not-allowed' : 'auto';
    };

    const getTextColor = () => {
      return destructive
        ? tokens.colors.text.error.tertiary
        : tokens.colors.text.secondary;
    };

    return {
      container: {
        width: 'inherit',
      },
      root: {
        borderColor: `${tokens.colors.border.tertiary} !important`,
        '&:hover .https-box': {
          borderColor: `${tokens.colors.border.tertiary} !important`,
        },
      },
      iconButton: {
        width: theme.sizeGuide['5xl'],
        height: theme.sizeGuide['5xl'],
        position: 'absolute !important',
        right: 0,
        marginRight: 0,
      },
      label: {
        fontSize: theme.typography.body_14_regular.fontSize,
        textAlign: 'left',
        marginBottom: theme.sizeGuide.sm,
        textTransform: 'capitalize',
      },
      textField: {
        width: '100%',
        '.MuiFormLabel-root': {
          color: `${destructive ? tokens.colors.text.error.tertiary : tokens.colors.text.placeholder} !important`,
        },
        '.MuiInputLabel-shrink': {
          color: `${destructive ? tokens.colors.text.error.tertiary : tokens.colors.text.primary} !important`,
        },
        '& .MuiInputBase-root': {
          fontSize: theme.typography.body_14_regular.fontSize,
          height: theme.sizeGuide['5xl'],
        },
        '& .MuiFormHelperText-root': {
          marginTop: theme.sizeGuide.sm,
          marginRight: theme.sizeGuide.none,
          display: 'block',
        },
        '.MuiTypography-root': {
          display: 'flex',
          gap: theme.sizeGuide.sm,
        },
        '.MuiInputBase-root.MuiInputBase-adornedEnd ': {
          paddingRight: theme.sizeGuide['5xl'],
        },
        '& .MuiInputBase-input': {
          width: '100%',
          padding: `10px ${theme.sizeGuide.lg}`,
          height: theme.sizeGuide['2xl'],
          '&:hover': {
            cursor: getHoverCursor(),
          },
          '&[type="password"]': {
            '::-ms-reveal': {
              display: 'none !important',
            },
            '::-webkit-input-placeholder': {
              display: 'none !important',
            },
          },
          '&::placeholder': {
            color: tokens.colors.text.placeholder,
          },
        },

        '& .MuiFormLabel-root': {
          width: 'auto',
          fontSize: theme.typography.body_14_regular.fontSize,
          lineHeight: theme.typography.body_14_regular.lineHeight,
          fontWeight: theme.typography.body_14_regular.fontWeight,
          top: '-5.5px',
          padding: '0',
        },
        '& .MuiInputLabel-shrink': {
          left: theme.sizeGuide.none,
          top: '1px',
          padding: `${theme.sizeGuide.none} ${theme.sizeGuide.xs}`,
          backgroundColor: 'white',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
          maxWidth: theme.sizeGuide.none,
        },

        '.MuiOutlinedInput-notchedOutline': {
          borderColor: getBorderColor(),
          '&:focus .MuiOutlinedInput-notchedOutline': {
            outline: '0',
          },
        },
        '& .MuiOutlinedInput-root': {
          background: disabled
            ? tokens.colors.bg.disabled
            : tokens.colors.bg.primary,

          borderRadius: theme.sizeGuide.xs,
          ':hover': {
            backgroundColor: tokens.colors.bg.primaryHover,
          },
          '&.Mui-focused': {
            backgroundColor: tokens.colors.bg.primary,
            boxShadow: 'none !important',
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${getFocusedBorderColor()}`,
          },
        },
      },
      icons: {
        height: theme.sizeGuide['2xl'],
        width: theme.sizeGuide['2xl'],
      },
      formControl: {
        width: '100%',
      },
      hintText: {
        marginTop: theme.sizeGuide.sm,
        fontSize: theme.typography.body_14_regular.fontSize,
        color: getTextColor(),
      },
    };
  }, [disabled, destructive, theme, tokens]);

  return styles;
};

export default useAuthAssetronInputFieldStyles;
